import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarIcon,
  Error as ErrorIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {useQuery, useLazyQuery, useMutation} from '@apollo/client';
import {GET_GUIDE_LANDING, GET_GUIDE_STARTER_INFO, START_JOURNEY} from '../graphql/queries';
import {useAuth} from '../contexts/AuthContext';
import logo from '../images/logo.png';
import colors from '../theme/colors';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

type PageState = 'loading' | 'ready' | 'error' | 'not_found';
type DialogStep = 'role' | 'email';

interface AvailableRole {
  roleId: string;
  name: string;
  description?: string | null;
}

export default function GuideLandingPage() {
  const navigate = useNavigate();
  const {user} = useAuth();
  const {orgSlug, guideSlug} = useParams<{
    orgSlug: string;
    guideSlug: string;
  }>();
  const [state, setState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // Dialog state
  const [modalOpen, setModalOpen] = useState(false);
  const [dialogStep, setDialogStep] = useState<DialogStep>('email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [starting, setStarting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Role selection state
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [availableRoles, setAvailableRoles] = useState<AvailableRole[]>([]);
  const [needsRoleSelection, setNeedsRoleSelection] = useState(false);

  const {data} = useQuery(GET_GUIDE_LANDING, {
    variables: {orgSlug: orgSlug || '', guideSlug: guideSlug || ''},
    skip: !orgSlug || !guideSlug,
    onCompleted: d => {
      if (!d.guideLanding) {
        setState('not_found');
      } else {
        setState('ready');
      }
    },
    onError: err => {
      setState('error');
      setErrorMessage(err.message || 'Failed to load page');
    },
  });

  const [fetchStarterInfo, {loading: starterInfoLoading}] = useLazyQuery(GET_GUIDE_STARTER_INFO, {
    onCompleted: d => {
      const info = d?.guideStarterInfo;
      if (info) {
        setNeedsRoleSelection(info.needsRoleSelection);
        setAvailableRoles(info.availableRoles || []);
        if (info.preSelectedRoleId) {
          setSelectedRoleId(info.preSelectedRoleId);
        }
        // If role selection is needed, show role picker first; otherwise go straight to email
        setDialogStep(info.needsRoleSelection ? 'role' : 'email');
      } else {
        setDialogStep('email');
      }
      setModalOpen(true);
    },
    onError: () => {
      // If starter info fails, just go straight to email
      setDialogStep('email');
      setModalOpen(true);
    },
  });

  const [startJourney] = useMutation(START_JOURNEY);

  useEffect(() => {
    if (!orgSlug || !guideSlug) {
      setState('error');
      setErrorMessage('Invalid URL');
    }
  }, [orgSlug, guideSlug]);

  const handleStartClick = () => {
    setEmail('');
    setEmailError('');
    setSelectedRoleId(null);
    setNeedsRoleSelection(false);
    setAvailableRoles([]);

    // Fetch starter info to determine if role selection is needed
    fetchStarterInfo({
      variables: {orgSlug: orgSlug || '', guideSlug: guideSlug || ''},
    });
  };

  const handleRoleContinue = () => {
    if (needsRoleSelection && !selectedRoleId) return;
    setDialogStep('email');
  };

  const handleStartJourney = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailError('');
    setStarting(true);

    try {
      const {data: result} = await startJourney({
        variables: {
          orgSlug,
          guideSlug,
          applicantEmail: trimmed,
          ...(selectedRoleId && {starterRoleId: selectedRoleId}),
        },
      });

      setModalOpen(false);
      const planId = result.startJourney?.planId;
      const isCurrentUser = user?.email?.toLowerCase() === trimmed;
      if (planId && isCurrentUser) {
        navigate(`/app/plans/${planId}`);
        return;
      }
      setSuccessMessage(
        result.startJourney.message ||
          'Check your email for a link to your journey.'
      );
    } catch (err: unknown) {
      setEmailError(
        err instanceof Error ? err.message : 'Something went wrong'
      );
    } finally {
      setStarting(false);
    }
  };

  const plp = data?.guideLanding;
  const resolvedTheme = plp?.resolvedTheme;
  const brandLogo = resolvedTheme?.logoUrl || logo;
  const headerLogo = resolvedTheme?.headerLogoUrl;
  const brandName = resolvedTheme?.organizationDisplayName || 'Planapus';
  const primaryColor = resolvedTheme?.primaryColor || colors.blue[600];
  const secondaryColor = resolvedTheme?.secondaryColor || colors.blue[50];
  const backgroundColor = resolvedTheme?.backgroundColor || colors.background;
  const textColor = resolvedTheme?.textColor || colors.textPrimary;
  const ctaTextColor = resolvedTheme?.ctaTextColor || '#ffffff';
  useDocumentTitle('Guides', plp?.title);

  // Loading
  if (state === 'loading') {
    return (
      <Container maxWidth="md" sx={{py: 8}}>
        <Card>
          <CardContent sx={{py: 4}}>
            <Box sx={{textAlign: 'center', mb: 4}}>
              <img
                src={logo}
                alt="Logo"
                style={{height: '50px', width: 'auto', marginBottom: '16px'}}
              />
            </Box>
            <Skeleton variant="text" height={48} sx={{mb: 2}} />
            <Skeleton variant="text" height={24} />
            <Skeleton variant="text" height={24} />
            <Skeleton variant="rectangular" height={200} sx={{my: 3}} />
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Not found
  if (state === 'not_found') {
    return (
      <Container maxWidth="sm" sx={{py: 8}}>
        <Card>
          <CardContent sx={{py: 4, textAlign: 'center'}}>
            <Box sx={{mb: 3}}>
              <img
                src={logo}
                alt="Logo"
                style={{height: '50px', width: 'auto', marginBottom: '16px'}}
              />
            </Box>
            <ErrorIcon sx={{fontSize: 60, color: 'warning.main', mb: 2}} />
            <Typography variant="h5" gutterBottom>
              Page Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary">
              The guide you're looking for doesn't exist or hasn't been
              published yet.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Error
  if (state === 'error') {
    return (
      <Container maxWidth="sm" sx={{py: 8}}>
        <Card>
          <CardContent sx={{py: 4, textAlign: 'center'}}>
            <Box sx={{mb: 3}}>
              <img
                src={logo}
                alt="Logo"
                style={{height: '50px', width: 'auto', marginBottom: '16px'}}
              />
            </Box>
            <ErrorIcon sx={{fontSize: 60, color: 'error.main', mb: 2}} />
            <Typography variant="h5" gutterBottom>
              Something Went Wrong
            </Typography>
            <Alert severity="error" sx={{mt: 2}}>
              {errorMessage}
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Ready
  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <Box sx={{bgcolor: backgroundColor, minHeight: '100vh', py: 4}}>
      {headerLogo && (
        <Box
          sx={{
            width: '100%',
            py: 1.5,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <img
            src={headerLogo}
            alt={`${brandName} header`}
            style={{maxHeight: 56, width: 'auto', objectFit: 'contain'}}
          />
        </Box>
      )}
      <Container maxWidth="md">
        {/* Logo */}
        <Box sx={{textAlign: 'center', mb: 4}}>
          <img
            src={brandLogo}
            alt={`${brandName} logo`}
            style={{height: '40px', width: 'auto'}}
          />
          <Typography sx={{mt: 1, fontSize: 13, color: colors.textSecondary}}>
            {brandName}
          </Typography>
        </Box>

        {/* Hero */}
        <Card sx={{mb: 3, overflow: 'visible'}}>
          <CardContent sx={{py: 5, px: 4}}>
            <Typography
              variant="h4"
              sx={{fontWeight: 700, color: textColor, mb: 2}}
            >
              {plp.title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: colors.textSecondary,
                fontSize: '1.1rem',
                lineHeight: 1.7,
              }}
            >
              {plp.description}
            </Typography>
          </CardContent>
        </Card>

        {/* Steps */}
        {plp.steps.length > 0 && (
          <Card sx={{mb: 3}}>
            <CardContent sx={{py: 4, px: 4}}>
              <Typography
                variant="h5"
                sx={{fontWeight: 600, color: textColor, mb: 3}}
              >
                What You'll Need to Do
              </Typography>
              <List disablePadding>
                {plp.steps.map((step: any) => (
                  <ListItem
                    key={step.id}
                    alignItems="flex-start"
                    sx={{
                      py: 2,
                      px: 0,
                      borderBottom: `1px solid ${colors.border}`,
                      '&:last-child': {borderBottom: 'none'},
                    }}
                  >
                    <ListItemIcon sx={{minWidth: 44, mt: 0.5}}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: secondaryColor,
                          color: primaryColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                        }}
                      >
                        {step.order}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle1"
                          sx={{fontWeight: 600, color: textColor}}
                        >
                          {step.title}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          sx={{color: colors.textSecondary, mt: 0.5}}
                        >
                          {step.summary}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* Deadlines */}
        {plp.deadlines.length > 0 && (
          <Card sx={{mb: 3}}>
            <CardContent sx={{py: 4, px: 4}}>
              <Typography
                variant="h5"
                sx={{fontWeight: 600, color: textColor, mb: 3}}
              >
                Key Dates
              </Typography>
              <List disablePadding>
                {plp.deadlines.map((d: any, i: number) => (
                  <ListItem
                    key={i}
                    sx={{
                      py: 1.5,
                      px: 0,
                      borderBottom: `1px solid ${colors.border}`,
                      '&:last-child': {borderBottom: 'none'},
                    }}
                  >
                    <ListItemIcon sx={{minWidth: 40}}>
                      <CalendarIcon
                        sx={{color: primaryColor, fontSize: 20}}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{fontWeight: 600}}
                          >
                            {d.label}
                          </Typography>
                          <Chip
                            label={formatDate(d.date)}
                            size="small"
                            sx={{
                              bgcolor: secondaryColor,
                              color: primaryColor,
                              fontWeight: 500,
                              fontSize: '0.75rem',
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        d.description ? (
                          <Typography
                            variant="body2"
                            sx={{color: colors.textSecondary, mt: 0.5}}
                          >
                            {d.description}
                          </Typography>
                        ) : undefined
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        )}

        {/* FAQ */}
        {plp.faqBlocks.length > 0 && (
          <Card sx={{mb: 3}}>
            <CardContent sx={{py: 4, px: 4}}>
              <Typography
                variant="h5"
                sx={{fontWeight: 600, color: textColor, mb: 3}}
              >
                Frequently Asked Questions
              </Typography>
              {plp.faqBlocks.map((faq: any, i: number) => (
                <Accordion
                  key={i}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: `1px solid ${colors.border}`,
                    '&:not(:last-child)': {mb: 1},
                    '&::before': {display: 'none'},
                    borderRadius: '8px !important',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{color: colors.textSecondary}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>
        )}

        {/* CTA / Success */}
        <Card sx={{mb: 4}}>
          <CardContent
            sx={{
              py: 5,
              px: 4,
              textAlign: 'center',
            }}
          >
            {successMessage ? (
              <>
                <CheckCircleIcon
                  sx={{fontSize: 60, color: colors.green[600], mb: 2}}
                />
                <Typography
                  variant="h6"
                  sx={{fontWeight: 600, color: textColor, mb: 1}}
                >
                  You're all set!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{color: colors.textSecondary}}
                >
                  {successMessage}
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  variant="h6"
                  sx={{fontWeight: 600, color: textColor, mb: 1}}
                >
                  Ready to get started?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{color: colors.textSecondary, mb: 3}}
                >
                  We'll guide you through each step so nothing falls through the
                  cracks.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStartClick}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: primaryColor,
                    color: ctaTextColor,
                    '&:hover': {opacity: 0.92, bgcolor: primaryColor},
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                  }}
                >
                  Start Your Application Journey
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Start Journey Dialog */}
        <Dialog
          open={modalOpen}
          onClose={() => !starting && setModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          {starterInfoLoading ? (
            <DialogContent sx={{py: 6, textAlign: 'center'}}>
              <CircularProgress size={32} />
            </DialogContent>
          ) : dialogStep === 'role' ? (
            <>
              <DialogTitle sx={{fontWeight: 600}}>
                Select Your Role
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{color: colors.textSecondary, mb: 2}}>
                  Choose the role that best describes you in this process.
                </Typography>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, mt: 1}}>
                  {availableRoles.map((role) => (
                    <Box
                      key={role.roleId}
                      onClick={() => setSelectedRoleId(role.roleId)}
                      sx={{
                        p: 2,
                        border: 2,
                        borderColor: selectedRoleId === role.roleId ? primaryColor : 'divider',
                        borderRadius: 2,
                        cursor: 'pointer',
                        bgcolor: selectedRoleId === role.roleId ? secondaryColor : 'transparent',
                        transition: 'all 0.15s',
                        '&:hover': {
                          borderColor: primaryColor,
                          bgcolor: secondaryColor,
                        },
                      }}
                    >
                      <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                        {role.name}
                      </Typography>
                      {role.description && (
                        <Typography variant="body2" sx={{color: colors.textSecondary, mt: 0.5}}>
                          {role.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </DialogContent>
              <DialogActions sx={{px: 3, pb: 2}}>
                <Button
                  onClick={() => setModalOpen(false)}
                  sx={{textTransform: 'none'}}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRoleContinue}
                  disabled={!selectedRoleId}
                  sx={{
                    bgcolor: primaryColor,
                    color: ctaTextColor,
                    '&:hover': {opacity: 0.92, bgcolor: primaryColor},
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Continue
                </Button>
              </DialogActions>
            </>
          ) : (
            <>
              <DialogTitle sx={{fontWeight: 600}}>
                Start Your Journey
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{color: colors.textSecondary, mb: 2}}>
                  Enter your email address to begin. We'll create a personalized
                  checklist so you can track your progress.
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  type="email"
                  label="Email address"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    setEmailError('');
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleStartJourney();
                  }}
                  error={!!emailError}
                  helperText={emailError}
                  disabled={starting}
                  sx={{mt: 1}}
                />

                {/* Show roles and which one the email will be assigned to */}
                {availableRoles.length > 0 && (
                  <Box sx={{mt: 3}}>
                    <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1.5}}>
                      Roles in this plan
                    </Typography>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                      {availableRoles.map((role) => {
                        const assignedRoleId = selectedRoleId || availableRoles[0]?.roleId;
                        const isAssigned = role.roleId === assignedRoleId;
                        return (
                          <Box
                            key={role.roleId}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              p: 1.5,
                              borderRadius: 1.5,
                              border: 1,
                              borderColor: isAssigned ? primaryColor : 'divider',
                              bgcolor: isAssigned ? secondaryColor : 'transparent',
                            }}
                          >
                            <PersonIcon sx={{fontSize: 20, color: isAssigned ? primaryColor : colors.textSecondary}} />
                            <Box sx={{flex: 1}}>
                              <Typography variant="body2" sx={{fontWeight: 600}}>
                                {role.name}
                              </Typography>
                              {role.description && (
                                <Typography variant="caption" sx={{color: colors.textSecondary}}>
                                  {role.description}
                                </Typography>
                              )}
                            </Box>
                            {isAssigned && (
                              <Chip
                                label={email.trim() || 'You'}
                                size="small"
                                sx={{
                                  bgcolor: secondaryColor,
                                  color: primaryColor,
                                  fontWeight: 500,
                                  fontSize: '0.75rem',
                                  maxWidth: 180,
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{px: 3, pb: 2}}>
                <Button
                  onClick={() => needsRoleSelection ? setDialogStep('role') : setModalOpen(false)}
                  disabled={starting}
                  sx={{textTransform: 'none'}}
                >
                  {needsRoleSelection ? 'Back' : 'Cancel'}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleStartJourney}
                  disabled={starting}
                  sx={{
                    bgcolor: primaryColor,
                    color: ctaTextColor,
                    '&:hover': {opacity: 0.92, bgcolor: primaryColor},
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {starting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Begin'
                  )}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Container>
    </Box>
  );
}
